import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ONBOARDING } from "@/constants/testIds";
import { toast } from "sonner";

const MODULES = [
  { key: "policies", label: "Políticas y Evidencia" },
  { key: "vacations", label: "Control de Vacaciones" },
  { key: "termination", label: "Aviso de Rescisión Asistido" },
  { key: "strategic_checkpoint", label: "Considera Antes de Decidir" },
  { key: "ai_advisors", label: "Asesores IA + Consultoría con un profesional de RH" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [industry, setIndustry] = useState("");
  const [size, setSize] = useState("");
  const [workWeek, setWorkWeek] = useState("mon_fri");
  const [modules, setModules] = useState(MODULES.map((m) => m.key));
  const [loading, setLoading] = useState(false);

  const toggleModule = (key) =>
    setModules((prev) => (prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/onboarding/complete", { industry, size, work_week: workWeek, modules_enabled: modules });
      await refreshUser();
      toast.success("Perfil de empresa completado");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <ShieldCheck className="w-7 h-7 text-petroleum" strokeWidth={1.5} />
          <span className="font-heading text-xl font-bold text-charcoal">CERH</span>
        </div>
        <div className="bg-card border border-petroleum/10 rounded-2xl p-8 shadow-[0_8px_30px_rgba(14,79,79,0.08)]">
          <h1 className="font-heading text-2xl font-bold text-charcoal mb-1">Cuéntanos de tu empresa</h1>
          <p className="text-sm text-charcoal/60 mb-6">Esto define qué módulos activaremos para ti.</p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label>Industria</Label>
              <input data-testid={ONBOARDING.industrySelect} required value={industry} onChange={(e) => setIndustry(e.target.value)}
                className="w-full mt-1 border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm" placeholder="Ej. Manufactura, Comercio, Servicios" />
            </div>
            <div>
              <Label>Número de empleados</Label>
              <select data-testid={ONBOARDING.sizeSelect} required value={size} onChange={(e) => setSize(e.target.value)}
                className="w-full mt-1 border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm">
                <option value="">Selecciona un rango</option>
                <option value="1-10">1 a 10 empleados</option>
                <option value="11-15">11 a 15 empleados</option>
                <option value="16-20">16 a 20 empleados</option>
                <option value="21-30">21 a 30 empleados</option>
                <option value="31-50">31 a 50 empleados</option>
                <option value="51+">51+ empleados</option>
              </select>
            </div>
            <div>
              <Label>Días laborables de la empresa</Label>
              <select data-testid="onboarding-work-week-select" value={workWeek} onChange={(e) => setWorkWeek(e.target.value)}
                className="w-full mt-1 border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm">
                <option value="mon_fri">Lunes a Viernes</option>
                <option value="mon_sat">Lunes a Sábado</option>
                <option value="mon_sun">Lunes a Domingo</option>
              </select>
              <p className="text-xs text-charcoal/50 mt-1">Se usa para calcular automáticamente los días de vacaciones solicitados.</p>
            </div>
            <div>
              <Label className="mb-2 block">Módulos a activar</Label>
              <div className="space-y-2">
                {MODULES.map((m) => (
                  <label key={m.key} className="flex items-center gap-3 text-sm text-charcoal/80 cursor-pointer">
                    <Checkbox data-testid={`${ONBOARDING.moduleCheckbox}-${m.key}`} checked={modules.includes(m.key)} onCheckedChange={() => toggleModule(m.key)} />
                    {m.label}
                  </label>
                ))}
              </div>
            </div>
            <Button data-testid={ONBOARDING.submitButton} type="submit" disabled={loading} className="w-full bg-petroleum hover:bg-petroleum/90 rounded-full h-11">
              {loading ? "Guardando..." : "Continuar al panel"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
